import {
  ExcalLogo,
  eyeIcon,
  TrashIcon,
  PlusIcon,
  ImageIcon,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import React from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { LanguageList } from "../app-language/LanguageList";

import { saveDebugState } from "./DebugCanvas";

// Simple film-strip icon for the Animate entry.
const animateIcon = (
  <svg
    aria-hidden="true"
    focusable="false"
    role="img"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="20"
    height="20"
  >
    <rect x="2" y="6" width="20" height="12" rx="2" ry="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
    <line x1="2" y1="14" x2="22" y2="14" />
    <line x1="6" y1="6" x2="6" y2="18" />
    <line x1="18" y1="6" x2="18" y2="18" />
  </svg>
);

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  setTheme: (theme: Theme | "system") => void;
  refresh: () => void;
  onOpenMyDiagrams: () => void;
  onOpenManageLibrary: () => void;
  onOpenMediaLibrary: () => void;
  onClearCanvas: () => void;
  onNewDiagram: () => void;
  onOpenAnimate: () => void;
}> = React.memo((props) => {
  return (
    <MainMenu>
      {/* "New diagram": empty canvas + reset URL hash + reset ref, so the
          next stroke creates a fresh dashboard record. Distinct from
          "Clear canvas" which keeps you on the SAME diagram and just empties
          its content (autosave PUTs the empty state to the same id). */}
      <MainMenu.Item icon={PlusIcon} onSelect={props.onNewDiagram}>
        New diagram
      </MainMenu.Item>
      <MainMenu.Item icon={ExcalLogo} onSelect={props.onOpenMyDiagrams}>
        Open
      </MainMenu.Item>
      <MainMenu.Item icon={ExcalLogo} onSelect={props.onOpenManageLibrary}>
        Manage library
      </MainMenu.Item>
      <MainMenu.Item icon={ImageIcon} onSelect={props.onOpenMediaLibrary}>
        Media library
      </MainMenu.Item>
      <MainMenu.Item icon={animateIcon} onSelect={props.onOpenAnimate}>
        Animate
      </MainMenu.Item>
      <MainMenu.DefaultItems.SaveAsImage />
      {props.isCollabEnabled && (
        <MainMenu.DefaultItems.LiveCollaborationTrigger
          isCollaborating={props.isCollaborating}
          onSelect={() => props.onCollabDialogOpen()}
        />
      )}
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.Item icon={TrashIcon} onSelect={props.onClearCanvas}>
        Clear canvas
      </MainMenu.Item>
      <MainMenu.Separator />
      {/* super-excalidraw: Excalidraw+ promo + Socials (X / Discord / YouTube)
          removed for the self-hosted build. */}
      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onClick={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
      <MainMenu.DefaultItems.ToggleTheme
        allowSystemTheme
        theme={props.theme}
        onSelect={props.setTheme}
      />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
    </MainMenu>
  );
});
